import { useEffect, useRef, useState } from 'react';
import SaveToListModalRedesign from '@/modules/dashboard/components/SaveToListModal/SaveToListModalRedesign';
import { getBusinessesLocal } from '@/services/businessService';
import {
	dashboardAddManyToSaveList,
	dashboardRemoveAllFromSaveList,
	dashboardRemoveListRequest
} from '@/modules/dashboard/dashboardActions';
import type * as SearchTypes from '@/modules/search/searchTypes';
import { useDispatch } from 'react-redux';
import type {
	Business,
	BusinessQuery,
	List
} from '@/modules/dashboard/dashboardTypes';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import ListModal from '../dashboard/components/ListModal/ListModal';

interface SaveToListManagerProps {
	isOpen: boolean;
	onClose: () => void;
	largeListToSave: Business[] | undefined;
	query: BusinessQuery;
	onBusinessesSelected: (businesses: Business[]) => void;
	removeAllChecks: () => void;
	setSaveToList: (b: Business[]) => void;
	listChange: (list: string) => void;
	onListSelected: (listId: string | null) => void;
	saveToList: Business[] | undefined;
}

const SaveToListManager = ({
	isOpen,
	onClose,
	largeListToSave,
	query,
	onBusinessesSelected,
	removeAllChecks,
	setSaveToList,
	listChange,
	onListSelected,
	saveToList
}: SaveToListManagerProps) => {
	const dispatch = useDispatch();
	const removeList = (l: List) => dispatch(dashboardRemoveListRequest(l));

	const [
		saveListRadioInputValueSelected,
		setSaveListRadioInputValueSelected
	] = useState('');
	const [saveListSliderValue, setSaveListSliderValue] = useState(0);
	const [saveToListLoading, setSaveToListLoading] = useState(false);
	const [removeListConfirm, setRemoveListConfirm] = useState<List>();
	const [savedSearch, setSavedSearch] = useState<SearchTypes.Search>();
	const [editList, setEditList] = useState<List>();

	const selectListAmount = async (value: number) => {
		let businessesList = largeListToSave || [];

		setSaveToListLoading(true);
		const result = await saveLargeList(value);
		businessesList = result || [];
		setSaveToListLoading(false);

		const grouping = businessesList.slice(0, value);

		removeAllChecks();
		dispatch(dashboardRemoveAllFromSaveList());
		dispatch(dashboardAddManyToSaveList(grouping));

		onBusinessesSelected(grouping);
	};

	const saveLargeList = async (listCount: number) => {
		const cleanedQuery = { ...query };

		if (cleanedQuery.searches) {
			const includes = cleanedQuery.searches
				.filter(s => s.type === 0)
				.map(s => s.value);
			const excludes = cleanedQuery.searches
				.filter(s => s.type === 1)
				.map(s => s.value);

			if (includes.length)
				cleanedQuery.includes = JSON.stringify(includes);
			if (excludes.length)
				cleanedQuery.excludes = JSON.stringify(excludes);
		}

		const finalQuery = {
			...cleanedQuery,
			limit: listCount,
			skip: 0,
			searchName: cleanedQuery.locationName
		};

		try {
			const res = await getBusinessesLocal(finalQuery);
			return res.data.data.result;
		} catch (error) {
			console.error('Error fetching large list:', error);
			return [];
		}
	};

	useEffect(() => {
		if (isOpen) {
			const length = largeListToSave?.length || 0;
			setSaveListSliderValue(length);

			if (['50', '100', '200', '3000'].includes(String(length))) {
				setSaveListRadioInputValueSelected(String(length));
			} else {
				setSaveListRadioInputValueSelected('');
			}
		}
	}, [isOpen, largeListToSave?.length]);

	const wrapperRef = useRef<HTMLDivElement>(null);

	useOnClickOutside({
		ref: wrapperRef,
		handler: (event?: MouseEvent | TouchEvent) => {
			const target = event?.target as HTMLElement;
			// TODO: Improve querySelector to be more specific if needed
			const listModal = document.querySelector('.modali');
			if (listModal && listModal.contains(target)) return; // Prevent close if click was inside ListModal
			onClose();
		},
		enabled: isOpen
	});

	if (!isOpen) return null;

	return (
		<div ref={wrapperRef}>
			<SaveToListModalRedesign
				largeListToSave={largeListToSave}
				closeSaveToList={onClose}
				saveListRadioInputValueSelected={
					saveListRadioInputValueSelected
				}
				setSaveListRadioInputValueSelected={
					setSaveListRadioInputValueSelected
				}
				saveListSliderValue={saveListSliderValue}
				setSaveListSliderValue={setSaveListSliderValue}
				selectListAmount={selectListAmount}
				saveToListLoading={saveToListLoading}
				saveBusinessesToList={setSaveToList}
			/>

			{saveToList?.length > 0 && (
				<ListModal
					businesses={saveToList}
					saveBusinessToList={(b?: Business[]) =>
						setSaveToList(b || [])
					}
					removeListConfirm={removeListConfirm}
					setRemoveListConfirm={setRemoveListConfirm}
					removeList={removeList}
					savedSearch={savedSearch}
					editList={editList}
					setEditList={setEditList}
					setSavedSearch={setSavedSearch}
					listChange={listChange}
					onListSelected={onListSelected}
				/>
			)}
		</div>
	);
};

export default SaveToListManager;
